import React, {useEffect, useState, useRef} from 'react';
import Modal from 'react-modal';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { beginPasskeyAutofill, useDevicePasskey, securityError } from './passkeys';
import {sectors} from './sectors';
import {disciplines} from './disciplines';
import {countries} from './countries'; // Use the static list
import './LoginRegisterPage.css';

const ARCHIVIST_EMAIL = 'connaught361archive@gmail.com';

const LoginRegisterPage = ({isOpen, closeModal, setUser}) => {
    const devicePasskey = useDevicePasskey();
    const stopAutofill = useRef(() => {});
    const navigate = useNavigate();
    const location = useLocation();
    const [remember, setRemember] = useState(false);
    const [busy, setBusy] = useState(false);
    const finishLogin = (data, password) => {
        setUser(data, { password });
        setError(''); setSuccessMessage(''); closeModal();
        const destination = location.state?.returnTo || sessionStorage.getItem('loginReturnTo');
        sessionStorage.removeItem('loginReturnTo');
        if (destination?.startsWith('/') && !destination.startsWith('//')) navigate(destination, { replace: true });
    };
    const [isRegistering, setIsRegistering] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        username: '',
        password: '',
        sector: '',
        discipline: '',
        country: ''
    });
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (!isOpen || isRegistering || window.location.hostname === '127.0.0.1') return;
        const cancel = beginPasskeyAutofill({ remember,
            onSelected: () => setBusy(true),
            onSuccess: data => { finishLogin(data, false); setBusy(false); },
            onError: error => { setError(securityError(error)); setBusy(false); }
        });
        stopAutofill.current = cancel;
        return cancel;
    }, [isOpen, isRegistering, remember]);

    const buildActivationEmail = () => {
        const fullName = `${formData.firstName} ${formData.lastName}`.trim();
        const subject = encodeURIComponent('Connaught361 account activation request');
        const body = encodeURIComponent(
            `Dear Archivist,\n\nPlease activate my Connaught361 archive account.\n\nName: ${fullName || '[your name]'}\nUsername: ${formData.username || '[username]'}\nEmail: ${formData.email || '[email]'}\n\nThank you.`
        );

        return `mailto:${ARCHIVIST_EMAIL}?subject=${subject}&body=${body}`;
    };

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        stopAutofill.current();
        const {username, password} = formData;
        setBusy(true);
        try {
            const response = await axios.post('/api/login', {username, password, remember});
            finishLogin(response.data, true);
        } catch (error) {
            setError(error.response?.data?.error || 'Unable to sign in. Please try again.');
            setSuccessMessage('');
            console.error('Error logging in:', error);
        } finally { setBusy(false); }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post('/api/register', formData);
            console.log('Register successful:', response.data);
            setUser(response.data); // Set the user state after registration
            localStorage.setItem('user', JSON.stringify(response.data)); // Store the user in localStorage
            setError(''); // Clear any previous error
            setSuccessMessage('Registration submitted. Please email the archivist so your account can be activated.');
            window.location.href = buildActivationEmail();
        } catch (error) {
            setError('Error registering. Please try again.');
            setSuccessMessage('');
            console.error('Error registering:', error);
        }
    };

    // Reset scroll position when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            window.scrollTo(0, 0);
        }
    }, [isOpen]);

    return (
        <Modal
            isOpen={isOpen}
            onAfterOpen={() => document.getElementById(isRegistering ? 'firstName' : 'username')?.focus()}
            onRequestClose={closeModal}
            contentLabel="Login/Register Modal"
            className="login-register-modal-content"
            overlayClassName="login-register-modal-overlay"
        >
            <div className="macos-window-controls">
                <div className="macos-button close" onClick={() => closeModal()}></div>
            </div>
            <div className="login-register-modal-header">
                <h2 className="login-register-title">{isRegistering ? 'Register' : 'Login'}</h2>
            </div>
            <div className="login-register-modal-body">
                {error && <div className="error-message">{error}</div>}
                {successMessage && <div className="success-message">{successMessage}</div>}
                {isRegistering ? (
                    <form onSubmit={handleRegister} className="login-register-form">
                        <div className="registration-help">
                            <strong>Account activation</strong>
                            <p>
                                After registering, email the archivist at{' '}
                                <a href={buildActivationEmail()}>{ARCHIVIST_EMAIL}</a>{' '}
                                so your account can be activated.
                            </p>
                        </div>
                        {/* Registration Form Fields */}
                        <div>
                            <label htmlFor="firstName" className="login-register-label">First Name:</label>
                            <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                required
                                className="login-register-input"
                            />
                        </div>
                        <div>
                            <label htmlFor="lastName" className="login-register-label">Last Name:</label>
                            <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                required
                                className="login-register-input"
                            />
                        </div>
                        <div>
                            <label htmlFor="email" className="login-register-label">Email:</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="login-register-input"
                            />
                        </div>
                        <div>
                            <label htmlFor="username" className="login-register-label">Username:</label>
                            <input
                                type="text"
                                id="username"
                                name="username"
                                autoComplete={isRegistering ? "username" : "username webauthn"}
                                autoFocus
                                value={formData.username}
                                onChange={handleChange}
                                required
                                className="login-register-input"
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="login-register-label">Password:</label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                autoComplete={isRegistering ? "new-password" : "current-password"}
                                value={formData.password}
                                onChange={handleChange}
                                required
                                className="login-register-input"
                            />
                        </div>
                        <div>
                            <label htmlFor="sector" className="login-register-label">Sector:</label>
                            <select
                                id="sector"
                                name="sector"
                                value={formData.sector}
                                onChange={handleChange}
                                required
                                className="login-register-select"
                            >
                                <option value="">Select a sector</option>
                                {sectors.map(sector => (
                                    <option key={sector} value={sector}>{sector}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="discipline" className="login-register-label">Discipline:</label>
                            <select
                                id="discipline"
                                name="discipline"
                                value={formData.discipline}
                                onChange={handleChange}
                                required
                                className="login-register-select"
                            >
                                <option value="">Select a discipline</option>
                                {disciplines.map(discipline => (
                                    <option key={discipline} value={discipline}>{discipline}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="country" className="login-register-label">Country:</label>
                            <select
                                id="country"
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                required
                                className="login-register-select"
                            >
                                <option value="">Select a country</option>
                                {countries.map(country => (
                                    <option key={country} value={country}>{country}</option>
                                ))}
                            </select>
                        </div>
                        <button type="submit" className="login-register-button">Register & Email Archivist</button>
                        <a
                            className="login-register-button login-register-mail-button"
                            href={buildActivationEmail()}
                        >
                            Open Email to Archivist
                        </a>
                        <button
                            type="button"
                            onClick={() => setIsRegistering(false)}
                            className="login-register-button"
                        >
                            Go to Login
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleLogin} className="login-register-form">
                        {devicePasskey === true && <p>Select your saved passkey in the username field to sign in with your face, fingerprint or device PIN. You can also use your password.</p>}
                        {/* Login Form Fields */}
                        <div>
                            <label htmlFor="username" className="login-register-label">Username:</label>
                            <input
                                type="text"
                                id="username"
                                name="username"
                                autoComplete={isRegistering ? "username" : "username webauthn"}
                                value={formData.username}
                                onChange={handleChange}
                                required
                                className="login-register-input"
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="login-register-label">Password:</label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                autoComplete={isRegistering ? "new-password" : "current-password"}
                                value={formData.password}
                                onChange={handleChange}
                                required
                                className="login-register-input"
                            />
                        </div>
                        <label className="login-register-label"><input type="checkbox" checked={remember} onChange={e=>setRemember(e.target.checked)}/> Remember this device for 30 days</label>
                        <small>Use this only on your own device.</small>
                        {devicePasskey === false && <p className="passkey-compatibility" role="status">Passkey sign-in is not available on this device or browser. You can still sign in with your username and password.</p>}
                        <button type="submit" disabled={busy} className="login-register-button">{busy ? 'Signing in…' : 'Sign in'}</button>

                        {window.location.hostname === '127.0.0.1' && <p>To test passkeys locally, open <a href="http://localhost:3000/">localhost:3000</a>.</p>}
                        <button
                            type="button"
                            onClick={() => setIsRegistering(true)}
                            className="login-register-button"
                        >
                            Create an account
                        </button>
                    </form>
                )}
            </div>
        </Modal>
    );
};

export default LoginRegisterPage;
